/**
 * Header scope switcher — shown when the user has multiple chapter/branch
 * memberships. Renders as an icon button that opens a modal scope picker.
 *
 * Hidden for users with zero or one effective scope (no decision to make).
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@/hooks/useAuth';
import { useMyMemberships } from '@/hooks/useCan';
import { chaptersApi, type ChapterSummary, type BranchSummary } from '@/services/api';

interface ScopeOption {
  key: string;
  label: string;
  href: string;
}

export function ScopeSwitcher() {
  const { isAuthenticated } = useAuth();
  const { memberships } = useMyMemberships();
  const navigate = useNavigate();
  const [chapters, setChapters] = useState<ChapterSummary[]>([]);
  const [branchesByChapter, setBranchesByChapter] = useState<Record<string, BranchSummary[]>>({});
  const [open, setOpen] = useState(false);

  // Load chapters for membership rows so we can render slugs as labels.
  useEffect(() => {
    if (!isAuthenticated || memberships.length === 0) return;
    chaptersApi.list('navarasam').then(setChapters).catch(() => undefined);
  }, [isAuthenticated, memberships.length]);

  // Lazy-load branches for any chapter membership we're going to render.
  useEffect(() => {
    const chapterIdsNeeded = new Set<string>();
    for (const m of memberships) {
      if (m.scopeType === 'branch') {
        for (const c of chapters) {
          chapterIdsNeeded.add(c.id);
        }
      }
    }
    for (const id of chapterIdsNeeded) {
      if (branchesByChapter[id] === undefined) {
        chaptersApi
          .listBranches(id)
          .then((rows) => setBranchesByChapter((prev) => ({ ...prev, [id]: rows })))
          .catch(() => setBranchesByChapter((prev) => ({ ...prev, [id]: [] })));
      }
    }
  }, [memberships, chapters]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isAuthenticated || memberships.length < 2) return null;

  const options: ScopeOption[] = [];
  for (const m of memberships) {
    if (m.scopeType === 'institution') {
      options.push({ key: `inst-${m.scopeId}`, label: 'Navarasam (institution)', href: '/admin' });
    } else if (m.scopeType === 'chapter') {
      const c = chapters.find((x) => x.id === m.scopeId);
      if (c) options.push({ key: `chapter-${c.id}`, label: c.displayName, href: `/c/${c.slug}` });
    } else if (m.scopeType === 'branch') {
      let label = `Branch ${m.scopeId.slice(0, 8)}…`;
      let href = '/';
      for (const c of chapters) {
        const b = (branchesByChapter[c.id] ?? []).find((x) => x.id === m.scopeId);
        if (b) {
          label = `${b.displayName ?? b.name} (${c.displayName})`;
          href = `/c/${c.slug}/b/${b.slug}`;
          break;
        }
      }
      options.push({ key: `branch-${m.scopeId}`, label, href });
    }
  }

  if (options.length < 2) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="p-2 rounded hover:bg-white/10 text-white/80"
        aria-label="Switch scope"
        title="Switch scope"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M8 7h12m0 0l-4-4m4 4l-4 4m-4 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 p-4 pt-20"
          onClick={() => setOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="card max-w-md w-full max-h-[80vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-white">Switch scope</h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-white/60 hover:text-white"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <ul className="space-y-1.5">
              {options.map((o) => (
                <li key={o.key}>
                  <button
                    type="button"
                    onClick={() => { setOpen(false); navigate(o.href); }}
                    className="w-full text-left px-3 py-2 rounded hover:bg-white/5 text-white/90"
                  >
                    {o.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}

export default ScopeSwitcher;
